import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';

import { CommonModule } from '@angular/common';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TranslateModule } from '@ngx-translate/core';

import type { Device, Side } from '@sites/entity-core';
import type { BlockDefaultForm } from '@sites/entity-signal-form';

import type { NormalizedDefaultBlockEntity } from '@web/types';

import { dayjs } from '@common/helpers/dayjs';
import { CSS_RESTRICTIONS } from '@common/constants';
import { EDisplayConditionType } from '@common/enums/display-condition.enum';

import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { SpecialBlocksService } from '@web/core/services/special-blocks.service';
import { BLOCK_DISPLAY_CONDITION_DEFAULT } from '@web/store/page-structure/blocks/blocks.models';

import { SettingsBodyComponent } from '../settings-body/settings-body.component';
import { SettingsBreadcrumbsComponent } from '../settings-breadcrumbs/settings-breadcrumbs.component';
import { WebDeviceToggleComponent } from '@web/shared/components/web-device-toggle/web-device-toggle.component';
import { WebSynchronizeStyleToggleComponent } from '@web/shared/components/web-synchronize-style-toggle';
import { WebColorPickerComponent } from '@web/shared/components/web-color-picker/web-color-picker.component';
import { SpInputNumComponent } from '@libs/components/sp-form-controls/sp-input-num/sp-input-num.component';
import { WebBackgroundImageSettingsComponent } from '@web/shared/components/web-background-image-settings/web-background-image-settings.component';
import { WebBackgroundVideoSettingsComponent } from '@web/shared/components/web-background-video-settings/web-background-video-settings.component';
import { SpSwitchComponent } from '@libs/ui/sp-switch/src/sp-switch.component';
import { SpAdvancedBorderSettingsComponent } from '@libs/components/sp-advanced-border-settings/sp-advanced-border-settings.component';
import { BlockLibraryAddonComponent } from './components/block-library-addon/block-library-addon.component';
import { WebCustomIdComponent } from '@web/shared/components/web-custom-id/web-custom-id.component';
import { BlockDisplayConditionSettingsComponent } from '@web/shared/components/web-settings/block-settings/block-display-condition-settings/block-display-condition-settings.component';

import { AbstractBlockSettingsComponent } from '../abstract-base-settings/abstract-block-settings.component';

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/** Which screen of the panel is showing. */
export enum BlockSettingsScreen {
    Main = 'main',
    DisplayCondition = 'display-condition',
}

/**
 * Settings for an ordinary block — see {@link AbstractBlockSettingsComponent}. On
 * top of the shared box it sizes by padding, and carries the width, the pinning and
 * the display condition.
 */
@Component({
    selector: 'web-block-settings',
    templateUrl: './block-settings.component.html',
    styleUrls: ['./block-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        SettingsBodyComponent,
        SettingsBreadcrumbsComponent,
        TooltipModule,
        WebDeviceToggleComponent,
        WebSynchronizeStyleToggleComponent,
        WebColorPickerComponent,
        SpInputNumComponent,
        WebBackgroundImageSettingsComponent,
        WebBackgroundVideoSettingsComponent,
        SpSwitchComponent,
        SpAdvancedBorderSettingsComponent,
        BlockLibraryAddonComponent,
        WebCustomIdComponent,
        TranslateModule,
        BlockDisplayConditionSettingsComponent,
    ],
})
export class BlockSettingsComponent
    extends AbstractBlockSettingsComponent<BlockDefaultForm, NormalizedDefaultBlockEntity>
    implements OnInit
{
    public readonly entity = input.required<NormalizedDefaultBlockEntity>();

    private readonly blocksFacade = inject(BlocksFacade);
    private readonly specialBlocksService = inject(SpecialBlocksService);

    public readonly screenEnum = BlockSettingsScreen;
    public readonly screen = signal<BlockSettingsScreen>(BlockSettingsScreen.Main);

    public readonly PADDING_MIN = CSS_RESTRICTIONS.BLOCK_PADDING_MIN;
    public readonly PADDING_MAX = CSS_RESTRICTIONS.BLOCK_PADDING_MAX;

    /** A block already pinned keeps its switch even if it is no longer first/last. */
    private readonly isFirstBlock = signal(false);
    private readonly isLastBlock = signal(false);

    public override ngOnInit(): void {
        super.ngOnInit();

        this.blocksFacade.isBlockFirst$(this.entity().id).subscribe((value) => this.isFirstBlock.set(value));
        this.blocksFacade.isBlockLast$(this.entity().id).subscribe((value) => this.isLastBlock.set(value));
    }

    // --- Reads ----------------------------------------------------------------

    public readonly padding = computed(() => this.at<{ value: Record<string, number> }>(this.form.padding)?.value);

    // The two switches are values now: full width is the viewport, full height is
    // a whole screen, and off is `null` — the content column and a height the
    // contents decide.
    public readonly isFullWidth = computed<boolean>(() => this.form.width().value()?.unit === 'vw');
    public readonly isFullHeight = computed<boolean>(() => this.form.height().value()?.desktop?.unit === 'vh');

    public readonly canPinToTop = computed<boolean>(() => this.isFirstBlock() || this.pinOnTheTop());
    public readonly canPinToBottom = computed<boolean>(() => this.isLastBlock() || this.pinOnTheBottom());

    public readonly displayCondition = computed(
        () => this.form.displayCondition().value() ?? BLOCK_DISPLAY_CONDITION_DEFAULT
    );

    /**
     * A CRM product block's id is generated, so only an admin may rename it.
     *
     * A `computed`, not a mapped `toSignal`: `toSignal` subscribes where it is
     * declared — in a field initializer, so during construction — and `isAdmin$`
     * replays synchronously, which put `this.entity()` on the required input
     * before the binding had ever run (NG0950). A computed is read from the
     * template, by which time it has.
     */
    public readonly isDisplayIdEditingDisabled = computed<boolean>(
        () => this.specialBlocksService.isCrmProductBlock(this.entity() as never) && !this.isAdmin()
    );

    // --- Writes ---------------------------------------------------------------

    protected copyDesktopBox(device: Device): void {
        this.form.setPadding(this.form.padding.desktop().value().value, device);
    }

    public setPadding(side: Side, value: number): void {
        this.write((device) => this.form.setPadding({ [side]: value }, device));
    }

    public setIsFullWidth(value: boolean): void {
        this.form.setWidth(value ? { value: 100, unit: 'vw' } : null);
    }

    /** The same on every screen, so it is written to all three breakpoints. */
    public setIsFullHeight(value: boolean): void {
        this.form.setHeightEverywhere(value ? { value: 100, unit: 'vh' } : null);
    }

    public setPinOnTheTop(value: boolean): void {
        this.form.setPin(value ? 'top' : null);
    }

    public setPinOnTheBottom(value: boolean): void {
        this.form.setPin(value ? 'bottom' : null);
    }

    public setDisplayCondition(value: unknown): void {
        this.form.setDisplayCondition(value as never);
    }

    // --- Screens --------------------------------------------------------------

    public togglePanelSettings(): void {
        this.screen.update((current) =>
            current === BlockSettingsScreen.Main ? BlockSettingsScreen.DisplayCondition : BlockSettingsScreen.Main
        );
    }

    // --- Display-condition summary --------------------------------------------

    private formatDate(date: Date): string {
        const parsed = new Date(date);
        const day = String(parsed.getDate()).padStart(2, '0');
        const month = String(parsed.getMonth() + 1).padStart(2, '0');
        const year = String(parsed.getFullYear()).slice(-2);

        return `${day}.${month}.${year}`;
    }

    private formatTime(date: string, timeZone: string): string {
        const tzDate = dayjs.utc(date).tz(timeZone);

        return `${String(tzDate.hour()).padStart(2, '0')}:${String(tzDate.minute()).padStart(2, '0')}`;
    }

    public getDisplayConditionValue(condition: any): string {
        const value = condition.value;

        if (condition.conditionType === EDisplayConditionType.DATE) {
            return `${this.formatDate(value.dateRange[0])} - ${this.formatDate(value.dateRange[1])}`;
        }

        if (condition.conditionType === EDisplayConditionType.TIME) {
            const [start, end] = value.timeRange;

            return `${this.formatTime(start, value.timeZone)} - ${this.formatTime(end, value.timeZone)}`;
        }

        if (condition.conditionType === EDisplayConditionType.GROUP_EDU) {
            return value.groupName;
        }

        if (condition.conditionType === EDisplayConditionType.WEEKDAY) {
            return this.translate.instant(WEEKDAYS[value.dayCount]);
        }

        // URL and the edu auth state carry their value verbatim.
        return value;
    }
}
